import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import { partition } from "../../common/utils/array.util";
import { useComponentState, useDebounce } from "../../utils/customHook.util";
import { takeIfAnyPredicateMatches } from "../../utils/filter.util";
import { wait } from "../../utils/ratelimit.util";
import { sortAbc } from "../../utils/sort.util";
import { stopEnterKeyPress } from "../../utils/window.util";
import HandleClicksOutside from "../HandleClicksOutside";
import Button from "./Buttons";
import { Flex, FlexInline } from "./Flex";
import Icon from "./Icon";
import Input, { type InputActions } from "./Input";
import "./Input.AutocompleteDropdown.css";
import Tag from "./Tag";

const minimumCharLengthToSearch = 2;

export type AutocompleteDropdownItem<T> = {
    id: T;
    text: string;
    selected: boolean;
};

type AutocompleteDropdownProps<T> = {
    label: string;
    placeholder?: string;
    items: AutocompleteDropdownItem<T>[];
    disabled?: boolean;
    isValid?: (value: string) => boolean;
    onChange: (selectedItems: AutocompleteDropdownItem<T>[]) => void;
    singleSelectionOnly?: boolean;
    sortOptions?: boolean;
    listenToItems?: boolean;
};

type AutocompleteDropdownState<T> = {
    isOpen: boolean;
    searchTerm: string | undefined;
    selectedItems: AutocompleteDropdownItem<T>[];
    visibleItems: AutocompleteDropdownItem<T>[];
};

function AutocompleteDropdown<T extends string>(props: AutocompleteDropdownProps<T>) {
    const {
        label,
        placeholder,
        items,
        disabled,
        isValid,
        onChange,
        singleSelectionOnly,
        sortOptions,
        listenToItems,
    } = props;
    const searchInputRef = useRef<InputActions>(null);
    const { state, setState } = useComponentState<AutocompleteDropdownState<T>>({
        isOpen: false,
        searchTerm: undefined,
        selectedItems: items.filter((it) => it.selected),
        visibleItems: items,
    });
    const debouncedSearchTerm = useDebounce<string | undefined>(state.searchTerm, 250);
    const showSelections = state.isOpen && state.selectedItems.length !== 0;
    const [sortedSelectedOptions, sortedUnselectedOptions] = partition<
        AutocompleteDropdownItem<T>
    >(
        sortOptions
            ? state.visibleItems.sort(sortAbc<AutocompleteDropdownItem<T>>("text"))
            : state.visibleItems,
        (visibleItem) =>
            state.selectedItems
                .map((selectedItem) => selectedItem.id)
                .includes(visibleItem.id),
    );
    const sortedOptions = sortOptions
        ? [...sortedSelectedOptions, ...sortedUnselectedOptions]
        : [
              ...state.visibleItems.filter((it) => it.selected),
              ...state.visibleItems.filter((it) => !it.selected),
          ];

    function getSearchInputReference() {
        const focus = () => searchInputRef.current?.focus();
        const reset = () => searchInputRef.current?.reset();

        return {
            focus,
            reset,
            waitAndFocus: wait(focus, 200),
        };
    }

    function openDropdown() {
        if (disabled) return;
        setState({ isOpen: true });
        getSearchInputReference().waitAndFocus();
    }

    function closeDropdown() {
        setState({ isOpen: false, searchTerm: undefined });
    }

    function clearSelections() {
        setState({ selectedItems: [] });
        onChange([]);
        getSearchInputReference().waitAndFocus();
    }

    function toggleSelectedItem(item: AutocompleteDropdownItem<T>, checked: boolean) {
        if (singleSelectionOnly) {
            setState({
                selectedItems: [item],
                isOpen: false,
            });
            onChange([item]);
            getSearchInputReference().reset();
            return;
        }

        const newSelectedItems = checked
            ? [...state.selectedItems, item]
            : [...state.selectedItems.filter((it) => it.id !== item.id)];

        setState({
            searchTerm: undefined,
            selectedItems: newSelectedItems,
        });
        onChange(newSelectedItems);
        getSearchInputReference().reset();
        getSearchInputReference().focus();
    }

    useEffect(() => {
        if (
            debouncedSearchTerm &&
            debouncedSearchTerm.length >= minimumCharLengthToSearch
        ) {
            setState({
                visibleItems: takeIfAnyPredicateMatches(items, debouncedSearchTerm, [
                    (item) => item.text,
                ]),
            });
        } else {
            setState({ visibleItems: items });
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (listenToItems) {
            setState({
                selectedItems: items.filter((it) => it.selected),
            });
        }
    }, [items]);

    return (
        <div
            className={classNames("autocomplete-dropdown", {
                "autocomplete-dropdown--with-selections": showSelections,
            })}
        >
            {!state.isOpen && (
                <Flex.Row
                    onClick={() => {
                        openDropdown();
                    }}
                >
                    <Input.TextField
                        label={label}
                        placeholder={placeholder}
                        disabled={disabled}
                        isValid={isValid}
                        value={state.selectedItems.map((it) => it.text).join(", ")}
                        readOnly
                    />
                    <span className="autocomplete-dropdown__expand-more">
                        <Icon.ExpandMore.Small />
                    </span>
                </Flex.Row>
            )}

            {state.isOpen && (
                <HandleClicksOutside
                    onClickOutside={() => {
                        closeDropdown();
                    }}
                    disabled={!state.isOpen}
                >
                    <Input.PersistentTextField
                        ref={searchInputRef}
                        label={label}
                        value={state.searchTerm || ""}
                        onChange={(newSearchTerm) => {
                            setState({ searchTerm: newSearchTerm });
                        }}
                        onKeyDown={stopEnterKeyPress}
                    />

                    <div className="autocomplete-dropdown__options">
                        {sortedOptions.map((item) => (
                            <div
                                key={item.id}
                                className="autocomplete-dropdown__option"
                            >
                                <Input.Checkbox
                                    label={item.text}
                                    checked={state.selectedItems
                                        .map((it) => it.id)
                                        .includes(item.id)}
                                    onChange={(checked) => {
                                        toggleSelectedItem(item, checked);
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    <span
                        className="autocomplete-dropdown__expand-more"
                        onClick={closeDropdown}
                    >
                        <Icon.ExpandLess.Small />
                    </span>

                    {showSelections && !singleSelectionOnly && (
                        <div className="autocomplete-dropdown__selections">
                            <Tag.Small.Bold.P90>
                                <FlexInline gap="small">
                                    {`${state.selectedItems.length} selected`}
                                    <Button.Ghost
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            clearSelections();
                                        }}
                                    >
                                        <Icon.Close.XSmall.P50 />
                                    </Button.Ghost>
                                </FlexInline>
                            </Tag.Small.Bold.P90>
                        </div>
                    )}
                </HandleClicksOutside>
            )}
        </div>
    );
}

export default AutocompleteDropdown;
